import { Button } from "@ui/Button";
import { CloseButton } from "@ui/CloseButton";
import { Dropdown } from "@ui/Dropdown";
import { Icon } from "@ui/Icon";
import { InputContainer } from "@ui/InputContainer";
import { InputGroup } from "@ui/InputGroup";
import xIcon from "@ui/icons/brand/xIcon.svg?raw";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { createMemo, type JSX, type ParentComponent } from "solid-js";
import {
	copyURLToClipboard,
	openShareDestination,
	openShareDestinationAfterDelay,
} from "~/modules/shared/utils/shareURL";
import {
	type CreatorProfileShareChannel,
	creatorProfileDisplayUrl,
	creatorProfileShareChannelUrl,
	creatorProfileShareUrl,
	creatorShareText,
	creatorShareTitle,
	SLACK_WEB_APP_URL,
} from "../creatorProfileShare";
import { askPageVars } from "../pageVars";
import "./ShareCreatorButton.css";

type ChannelButtonProps = {
	label: string;
	onClick: () => void;
	/** Grey icon well; default is mint/teal (WhatsApp, Messages). */
	iconTone?: "brand" | "muted";
	children: JSX.Element;
};

const ShareChannelButton: ParentComponent<ChannelButtonProps> = (props) => {
	return (
		<button
			type="button"
			class="share-creator-popover__channel"
			onClick={() => props.onClick()}
		>
			<span
				class="share-creator-popover__channel-icon"
				classList={{
					"share-creator-popover__channel-icon--muted":
						props.iconTone === "muted",
				}}
			>
				{props.children}
			</span>
			<Text
				as="span"
				size="xsmall"
				weight="semi-bold"
				color="ink"
				style={{ "line-height": "1.2" }}
			>
				{props.label}
			</Text>
		</button>
	);
};

export const ShareCreatorButton = () => {
	const vars = askPageVars();
	const shareUrl = createMemo(() =>
		creatorProfileShareUrl(vars.influencer_handle),
	);
	const displayUrl = createMemo(() =>
		creatorProfileDisplayUrl(vars.influencer_handle),
	);
	const shareTitle = createMemo(() =>
		creatorShareTitle(vars.influencer_display_name),
	);
	const shareText = createMemo(() =>
		creatorShareText(vars.influencer_display_name),
	);
	const visibilityNote = createMemo(
		() =>
			`Anyone with the link can see ${vars.influencer_display_name}'s page and ask their own questions.`,
	);

	const copyLink = () => {
		void copyURLToClipboard({
			url: shareUrl(),
			copiedDescription:
				"You can paste it into a chat, email, or document to share this creator.",
		});
	};

	const copyLinkThenOpenSite = async (
		openUrl: string,
		close: () => void,
		copyOptions: {
			copiedTitle: string;
			copiedDescription: string;
		},
	) => {
		await copyURLToClipboard({
			url: shareUrl(),
			...copyOptions,
		});
		close();
		openShareDestinationAfterDelay(openUrl);
	};

	const shareViaChannel = async (
		channel: CreatorProfileShareChannel,
		close: () => void,
	) => {
		const url = shareUrl();
		const title = shareTitle();
		const text = shareText();

		if (channel === "slack") {
			await copyLinkThenOpenSite(SLACK_WEB_APP_URL, close, {
				copiedTitle: "Link copied for Slack",
				copiedDescription: "Paste it into any channel or DM, then send.",
			});
			return;
		}

		const href = creatorProfileShareChannelUrl(channel, { url, title, text });
		if (href) {
			openShareDestination(href);
			close();
		}
	};

	return (
		<Dropdown
			class="share-creator-popover"
			placement="bottom end"
			aria-label="Share this creator"
			triggerVariant="tertiary"
			triggerSize="medium"
			listBoxStyle={{
				width: "min(28rem, calc(100vw - 2rem))",
			}}
			trigger={<MaterialSymbol symbol="ios_share" />}
			content={({ close }) => (
				<VStack spacing="large">
					<div class="share-creator-popover__header">
						<VStack spacing="small">
							<Text
								family="mono"
								size="xsmall"
								color="fg3"
								transform="uppercase"
							>
								Share this creator
							</Text>
							<Text size="standard" weight="semi-bold" color="ink">
								{shareTitle()}
							</Text>
						</VStack>
						<CloseButton onClick={close} />
					</div>

					<InputContainer grow class="share-creator-popover__link-field">
						<InputGroup
							prefix={
								<MaterialSymbol
									symbol="link"
									relativeSize="smaller"
									color="fg3"
								/>
							}
							suffix={
								<Button
									type="button"
									variant="primary"
									symbol="content_copy"
									onClick={() => copyLink()}
								>
									Copy
								</Button>
							}
						>
							<input
								type="text"
								readOnly
								value={displayUrl()}
								aria-label="Creator profile link"
							/>
						</InputGroup>
					</InputContainer>

					<div class="share-creator-popover__grid">
						<ShareChannelButton
							label="WhatsApp"
							onClick={() => void shareViaChannel("whatsapp", close)}
						>
							<MaterialSymbol symbol="chat" relativeSize="plus" />
						</ShareChannelButton>
						<ShareChannelButton
							label="Messages"
							onClick={() => void shareViaChannel("messages", close)}
						>
							<MaterialSymbol symbol="sms" relativeSize="plus" />
						</ShareChannelButton>
						<ShareChannelButton
							label="Email"
							iconTone="muted"
							onClick={() => void shareViaChannel("email", close)}
						>
							<MaterialSymbol symbol="mail" relativeSize="plus" />
						</ShareChannelButton>
						<ShareChannelButton
							label="X"
							iconTone="muted"
							onClick={() => void shareViaChannel("x", close)}
						>
							<Icon icon={xIcon} />
						</ShareChannelButton>
						<ShareChannelButton
							label="Facebook"
							iconTone="muted"
							onClick={() => void shareViaChannel("facebook", close)}
						>
							<MaterialSymbol symbol="public" relativeSize="plus" />
						</ShareChannelButton>
						<ShareChannelButton
							label="Reddit"
							iconTone="muted"
							onClick={() => void shareViaChannel("reddit", close)}
						>
							<MaterialSymbol symbol="forum" relativeSize="plus" />
						</ShareChannelButton>
						<ShareChannelButton
							label="LinkedIn"
							iconTone="muted"
							onClick={() => void shareViaChannel("linkedin", close)}
						>
							<MaterialSymbol symbol="work" relativeSize="plus" />
						</ShareChannelButton>
						<ShareChannelButton
							label="Telegram"
							iconTone="muted"
							onClick={() => void shareViaChannel("telegram", close)}
						>
							<MaterialSymbol symbol="send" relativeSize="plus" />
						</ShareChannelButton>
						<ShareChannelButton
							label="Slack"
							iconTone="muted"
							onClick={() => void shareViaChannel("slack", close)}
						>
							<MaterialSymbol symbol="tag" relativeSize="plus" />
						</ShareChannelButton>
					</div>

					<HStack
						spacing="small"
						align="start"
						class="share-creator-popover__footer"
					>
						<MaterialSymbol
							symbol="visibility"
							relativeSize="smaller"
							color="fg3"
						/>
						<Text size="xsmall" color="fg3">
							{visibilityNote()}
						</Text>
					</HStack>
				</VStack>
			)}
		/>
	);
};
